// =====================================================================
// Faz P (2026-05-06) — Site Trafiği
// =====================================================================
// "Site şu an ziyaret ediliyor mu, kim bakıyor neye?" sorusuna cevap.
// Owner sürekli sorabilir bunu — bu sayfa her 30 sn auto-refresh, canlı
// hissi verir.
//
// Endpoint: GET /api/analytics/traffic
// 5 panel:
//   1) Hero KPI — Şu an online + bugün toplam + dün ile kıyas
//   2) 4 periyot KPI (bugün/dün/7g/30g) view + session sayıları
//   3) Bugün saatlik bar (00-23)
//   4) Top 10 referrer (hangi siteden geliyorlar)
//   5) Son 20 ziyaretçi canlı feed (sayfa + kaynak + zaman)
// =====================================================================
import React, { useEffect, useState, useMemo } from "react";
import {
  Box, Card, Typography, Grid, Chip, Table, TableHead, TableRow, TableCell,
  TableBody, CircularProgress, Alert, IconButton, Tooltip, LinearProgress
} from "@mui/material";
import RefreshIcon from "@mui/icons-material/Refresh";
import VisibilityIcon from "@mui/icons-material/Visibility";
import LanguageIcon from "@mui/icons-material/Language";
import PhoneIphoneIcon from "@mui/icons-material/PhoneIphone";
import RadioButtonCheckedIcon from "@mui/icons-material/RadioButtonChecked";
import API from "../services/api";

const fmtNum = (v) => Number(v || 0).toLocaleString("tr-TR");

// Saniye → "az önce / X dk önce / X saat önce"
const timeAgo = (date) => {
  const sec = Math.floor((Date.now() - new Date(date)) / 1000);
  if (sec < 60) return `${sec} sn önce`;
  if (sec < 3600) return `${Math.floor(sec / 60)} dk önce`;
  if (sec < 86400) return `${Math.floor(sec / 3600)} sa önce`;
  return new Date(date).toLocaleString("tr-TR", { day: "2-digit", month: "short", hour: "2-digit", minute: "2-digit" });
};

// URL → kısa etiket (sayfa adı)
const pageLabel = (page) => {
  if (!page) return "—";
  if (page === "/") return "Anasayfa";
  if (page.startsWith("/urun/")) return "Ürün: " + page.replace("/urun/", "").slice(0, 40);
  if (page.startsWith("/kategori/")) return "Kategori: " + page.replace("/kategori/", "").slice(0, 40);
  if (page.includes("/sepet")) return "Sepet";
  if (page.includes("/checkout") || page.includes("/odeme")) return "Ödeme";
  if (page.includes("/urunler")) return "Tüm Ürünler";
  return page.slice(0, 50);
};

export default function SiteTraffic() {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [err, setErr] = useState(null);

  const load = () => {
    API.get("/api/analytics/traffic")
      .then(r => { setData(r.data); setErr(null); })
      .catch(e => setErr(e.response?.data?.error || e.message))
      .finally(() => setLoading(false));
  };

  // İlk yükleme + 30 sn auto-refresh
  useEffect(() => {
    load();
    const t = setInterval(load, 30000);
    return () => clearInterval(t);
  }, []);

  // Saatlik için max bulup oranlama (chart bar yüksekliği)
  // 2026-05-10: Endpoint shape değişirse beyaz ekran oluyordu — Array.isArray guard
  const hourlyMax = useMemo(() => {
    const arr = Array.isArray(data?.hourly) ? data.hourly : [];
    if (arr.length === 0) return 1;
    return Math.max(...arr.map(h => Number(h.views) || 0), 1);
  }, [data]);

  // 0-23 saat → views map (gap'leri doldur)
  const hourlyMap = useMemo(() => {
    const m = {};
    const arr = Array.isArray(data?.hourly) ? data.hourly : [];
    arr.forEach(h => { m[h.hour] = h; });
    return m;
  }, [data]);

  if (loading && !data) {
    return <Box sx={{ display: "flex", justifyContent: "center", p: 6 }}><CircularProgress /></Box>;
  }
  if (err) return <Alert severity="error" sx={{ m: 2 }}>{err}</Alert>;
  if (!data) return null;

  const p = data.periods || {};
  const totalSrc30d = (Number(p.web_views_30d) || 0) + (Number(p.mobile_views_30d) || 0);
  const webPct = totalSrc30d > 0 ? Math.round((p.web_views_30d / totalSrc30d) * 100) : 0;
  const mobPct = totalSrc30d > 0 ? Math.round((p.mobile_views_30d / totalSrc30d) * 100) : 0;

  // Bugün vs dün karşılaştırma
  const todayViews = Number(p.today_views) || 0;
  const yesterdayViews = Number(p.yesterday_views) || 0;
  const dayChange = yesterdayViews > 0 ? Math.round(((todayViews - yesterdayViews) / yesterdayViews) * 100) : null;

  return (
    <Box sx={{ p: { xs: 1, md: 2 } }}>
      <Box sx={{ display: "flex", alignItems: "center", justifyContent: "space-between", mb: 3 }}>
        <Box>
          <Typography variant="h4" fontWeight={800}>Site Trafiği</Typography>
          <Typography variant="body2" color="text.secondary">
            Canlı ziyaretçi takibi — 30 sn'de bir otomatik yenilenir
          </Typography>
        </Box>
        <Tooltip title="Şimdi yenile">
          <IconButton onClick={load}><RefreshIcon /></IconButton>
        </Tooltip>
      </Box>

      {/* HERO: Şu an online + bugün toplam + dün kıyas */}
      <Grid container spacing={2} mb={3}>
        <Grid item xs={12} md={4}>
          <Card sx={{
            p: 3, borderRadius: 3,
            background: "linear-gradient(135deg, #10b98122 0%, #10b98111 100%)",
            border: "2px solid #10b981", height: "100%"
          }}>
            <Box sx={{ display: "flex", alignItems: "center", gap: 1, mb: 1 }}>
              <Box sx={{
                width: 10, height: 10, borderRadius: "50%", bgcolor: "#10b981",
                animation: data.online_now > 0 ? "pulse 1.5s ease-in-out infinite" : "none",
                "@keyframes pulse": {
                  "0%, 100%": { opacity: 1 },
                  "50%": { opacity: 0.4 }
                }
              }} />
              <Typography variant="caption" fontWeight={700} sx={{ color: "#10b981", letterSpacing: 1.5 }}>
                ŞU AN ONLINE
              </Typography>
            </Box>
            <Typography variant="h2" fontWeight={900} sx={{ color: "#10b981", lineHeight: 1.1 }}>
              {data.online_now}
            </Typography>
            <Typography variant="caption" color="text.secondary" mt={1} display="block">
              Son 5 dakikada aktif unique oturum
            </Typography>
          </Card>
        </Grid>
        <Grid item xs={6} md={4}>
          <Card sx={{ p: 3, borderRadius: 3, height: "100%" }}>
            <Typography variant="caption" color="text.secondary" fontWeight={700}>
              BUGÜN
            </Typography>
            <Typography variant="h3" fontWeight={800} color="#1565C0" sx={{ lineHeight: 1.1 }}>
              {fmtNum(todayViews)}
            </Typography>
            <Typography variant="caption" color="text.disabled" display="block" mt={0.5}>
              {fmtNum(p.today_sessions)} oturum · {fmtNum(p.today_ips)} farklı IP
            </Typography>
            {dayChange != null && (
              <Chip
                size="small"
                label={`${dayChange >= 0 ? "▲ +" : "▼ "}%${Math.abs(dayChange)}`}
                sx={{
                  mt: 1,
                  bgcolor: dayChange >= 0 ? "#10b981" : "#ef4444",
                  color: "white", fontWeight: 700
                }}
              />
            )}
          </Card>
        </Grid>
        <Grid item xs={6} md={4}>
          <Card sx={{ p: 3, borderRadius: 3, height: "100%" }}>
            <Typography variant="caption" color="text.secondary" fontWeight={700}>
              DÜN
            </Typography>
            <Typography variant="h3" fontWeight={800} color="#64748b" sx={{ lineHeight: 1.1 }}>
              {fmtNum(yesterdayViews)}
            </Typography>
            <Typography variant="caption" color="text.disabled" display="block" mt={0.5}>
              {fmtNum(p.yesterday_sessions)} oturum
            </Typography>
          </Card>
        </Grid>
      </Grid>

      {/* Periyot KPI: 7g + 30g + Web/Mobile */}
      <Grid container spacing={2} mb={3}>
        <Grid item xs={6} md={3}>
          <Card sx={{ p: 2, borderRadius: 3 }}>
            <Typography variant="caption" color="text.secondary">Son 7 Gün</Typography>
            <Typography variant="h5" fontWeight={800}>{fmtNum(p.week_views)}</Typography>
            <Typography variant="caption" color="text.disabled">{fmtNum(p.week_sessions)} oturum</Typography>
          </Card>
        </Grid>
        <Grid item xs={6} md={3}>
          <Card sx={{ p: 2, borderRadius: 3 }}>
            <Typography variant="caption" color="text.secondary">Son 30 Gün</Typography>
            <Typography variant="h5" fontWeight={800}>{fmtNum(p.month_views)}</Typography>
            <Typography variant="caption" color="text.disabled">{fmtNum(p.month_sessions)} oturum</Typography>
          </Card>
        </Grid>
        <Grid item xs={12} md={6}>
          <Card sx={{ p: 2, borderRadius: 3 }}>
            <Typography variant="caption" color="text.secondary" fontWeight={700}>
              KAYNAK DAĞILIMI (30 gün)
            </Typography>
            <Box sx={{ mt: 1 }}>
              <Box sx={{ display: "flex", alignItems: "center", gap: 1, mb: 0.5 }}>
                <LanguageIcon sx={{ color: "#3b82f6", fontSize: 18 }} />
                <Typography variant="body2" sx={{ flex: 1 }}>Web</Typography>
                <Typography fontWeight={700} fontSize={13}>{fmtNum(p.web_views_30d)} (%{webPct})</Typography>
              </Box>
              <LinearProgress
                variant="determinate" value={webPct}
                sx={{ height: 6, borderRadius: 3, mb: 1.5,
                  "& .MuiLinearProgress-bar": { bgcolor: "#3b82f6" } }}
              />
              <Box sx={{ display: "flex", alignItems: "center", gap: 1, mb: 0.5 }}>
                <PhoneIphoneIcon sx={{ color: "#10b981", fontSize: 18 }} />
                <Typography variant="body2" sx={{ flex: 1 }}>Mobil App</Typography>
                <Typography fontWeight={700} fontSize={13}>{fmtNum(p.mobile_views_30d)} (%{mobPct})</Typography>
              </Box>
              <LinearProgress
                variant="determinate" value={mobPct}
                sx={{ height: 6, borderRadius: 3,
                  "& .MuiLinearProgress-bar": { bgcolor: "#10b981" } }}
              />
            </Box>
          </Card>
        </Grid>
      </Grid>

      {/* Saatlik bar chart */}
      <Card sx={{ p: 2.5, borderRadius: 3, mb: 3 }}>
        <Typography variant="h6" fontWeight={700} mb={2}>Bugün Saatlik Dağılım (00-23)</Typography>
        <Box sx={{ display: "flex", alignItems: "flex-end", gap: 0.5, height: 120 }}>
          {Array.from({ length: 24 }, (_, h) => {
            const d = hourlyMap[h];
            const views = d ? Number(d.views) : 0;
            const heightPct = (views / hourlyMax) * 100;
            const isCurrentHour = h === new Date().getHours();
            return (
              <Tooltip
                key={h}
                title={`${String(h).padStart(2, "0")}:00 — ${fmtNum(views)} görüntüleme${d ? ` (${d.sessions} oturum)` : ""}`}
                placement="top"
              >
                <Box sx={{ flex: 1, display: "flex", flexDirection: "column", alignItems: "center", gap: 0.5 }}>
                  <Box sx={{
                    width: "100%", height: `${Math.max(heightPct, 2)}%`,
                    bgcolor: isCurrentHour ? "#10b981" : "#1565C0",
                    borderRadius: "3px 3px 0 0", transition: "height 0.3s"
                  }} />
                  <Typography variant="caption" sx={{ fontSize: 9, color: "text.disabled" }}>
                    {String(h).padStart(2, "0")}
                  </Typography>
                </Box>
              </Tooltip>
            );
          })}
        </Box>
      </Card>

      <Grid container spacing={2}>
        {/* Top Referrer */}
        <Grid item xs={12} lg={5}>
          <Card sx={{ borderRadius: 3, overflow: "hidden", height: "100%" }}>
            <Box sx={{ p: 2, bgcolor: "#f8fafc", borderBottom: "1px solid #e2e8f0" }}>
              <Typography variant="h6" fontWeight={700}>Top 10 Kaynak (30 gün)</Typography>
            </Box>
            {data.referrers.length === 0 ? (
              <Box sx={{ p: 3 }}><Typography color="text.secondary">Veri yok</Typography></Box>
            ) : (
              <Table size="small">
                <TableHead>
                  <TableRow>
                    <TableCell><strong>Kaynak</strong></TableCell>
                    <TableCell align="right"><strong>Görüntüleme</strong></TableCell>
                    <TableCell align="right"><strong>Oturum</strong></TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {(Array.isArray(data.referrers) ? data.referrers : []).map((r, i) => (
                    <TableRow key={i} hover>
                      <TableCell sx={{ fontSize: 13 }}>
                        {r.source_domain === "(direct)" ? (
                          <Chip label="Direkt / Bookmark" size="small" sx={{ bgcolor: "#10b98122", color: "#10b981", fontWeight: 600 }} />
                        ) : (
                          <Typography fontFamily="monospace" fontSize={12}>{r.source_domain}</Typography>
                        )}
                      </TableCell>
                      <TableCell align="right" sx={{ fontWeight: 700 }}>{fmtNum(r.visits)}</TableCell>
                      <TableCell align="right">{fmtNum(r.sessions)}</TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            )}
          </Card>
        </Grid>

        {/* Recent visitors feed */}
        <Grid item xs={12} lg={7}>
          <Card sx={{ borderRadius: 3, overflow: "hidden", height: "100%" }}>
            <Box sx={{ p: 2, bgcolor: "#f8fafc", borderBottom: "1px solid #e2e8f0", display: "flex", alignItems: "center", gap: 1 }}>
              <RadioButtonCheckedIcon sx={{ color: "#ef4444", fontSize: 14, animation: "pulse 1.5s ease-in-out infinite" }} />
              <Typography variant="h6" fontWeight={700}>Son Ziyaretçiler (canlı)</Typography>
            </Box>
            {data.recent.length === 0 ? (
              <Box sx={{ p: 3 }}><Typography color="text.secondary">Son 6 saatte ziyaret yok</Typography></Box>
            ) : (
              <Table size="small">
                <TableHead>
                  <TableRow>
                    <TableCell width={60}><strong>Kaynak</strong></TableCell>
                    <TableCell><strong>Sayfa</strong></TableCell>
                    <TableCell><strong>Ziyaretçi</strong></TableCell>
                    <TableCell align="right"><strong>Zaman</strong></TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {(Array.isArray(data.recent) ? data.recent : []).map((v, i) => (
                    <TableRow key={i} hover>
                      <TableCell>
                        {v.source === "mobile" ? (
                          <PhoneIphoneIcon sx={{ color: "#10b981", fontSize: 18 }} />
                        ) : (
                          <LanguageIcon sx={{ color: "#3b82f6", fontSize: 18 }} />
                        )}
                      </TableCell>
                      <TableCell sx={{ fontSize: 12 }}>
                        <Box sx={{ display: "flex", alignItems: "center", gap: 0.5 }}>
                          <VisibilityIcon sx={{ fontSize: 14, color: "#94a3b8" }} />
                          <Typography fontSize={12} sx={{ lineHeight: 1.2 }}>
                            {pageLabel(v.page)}
                          </Typography>
                        </Box>
                      </TableCell>
                      <TableCell sx={{ fontSize: 12 }}>
                        {v.user_name ? (
                          <Box>
                            <Typography fontSize={12} fontWeight={600}>{v.company_name || v.user_name}</Typography>
                            <Typography variant="caption" color="text.disabled" fontSize={10}>üye</Typography>
                          </Box>
                        ) : (
                          <Typography variant="caption" color="text.disabled" fontFamily="monospace" fontSize={11}>
                            {v.ip_address || "anonim"}
                          </Typography>
                        )}
                      </TableCell>
                      <TableCell align="right" sx={{ fontSize: 11, color: "text.secondary" }}>
                        {timeAgo(v.created_at)}
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            )}
          </Card>
        </Grid>
      </Grid>
    </Box>
  );
}
